import React, { useState, useEffect } from 'react';
import { 
  Search, Plus, Calendar, Users, CreditCard, FileText,
  Clock, CheckCircle, Phone, Mail, ArrowRight, RefreshCw,
  User, Printer, Send, LogOut, Menu, X, AlertCircle
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a01f8d12-1bde-4fd2-8a5f-4dafd263008a/artifacts/d665bj3h_Global%20Wings%20fond%20noir.png";
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const ReceptionDashboard = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [todaysAppointments, setTodaysAppointments] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [destinations, setDestinations] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showNewVisaModal, setShowNewVisaModal] = useState(false);
  const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showProspectModal, setShowProspectModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, appointmentsRes, destinationsRes, agentsRes] = await Promise.all([
        api.get('/reception/stats'),
        api.get('/reception/todays-appointments'),
        api.get('/visa-destinations'),
        api.get('/reception/agents')
      ]);
      setStats(statsRes.data);
      setTodaysAppointments(appointmentsRes.data);
      setDestinations(destinationsRes.data);
      setAgents(agentsRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (searchQuery.length < 2) return;
    try {
      const response = await api.get(`/reception/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Erreur de recherche');
    }
  };

  // New Visa Modal
  const NewVisaModal = () => {
    const [formData, setFormData] = useState({
      destination_id: '',
      visa_type_id: '',
      applicant_name: '',
      applicant_email: '',
      applicant_phone: '',
      travel_date: '',
      visa_urgency: 'normal',
      notes: ''
    });
    const [selectedDestination, setSelectedDestination] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const handleDestinationChange = (destId) => {
      const dest = destinations.find(d => d.id === destId);
      setSelectedDestination(dest);
      setFormData({ ...formData, destination_id: destId, visa_type_id: '' });
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.destination_id || !formData.visa_type_id || !formData.applicant_name || !formData.applicant_phone) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }
      setSubmitting(true);
      try {
        const response = await api.post('/reception/quick-visa', formData);
        toast.success(`Dossier créé ! Réf: ${response.data.reference}`);
        setShowNewVisaModal(false);
        fetchData();
      } catch (error) {
        toast.error('Erreur lors de la création');
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <Dialog open={showNewVisaModal} onOpenChange={setShowNewVisaModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-[#0F172A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#D4AF37]" />
              Nouveau Dossier Visa
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="new-visa-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Destination *</Label>
                <Select value={formData.destination_id} onValueChange={handleDestinationChange}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10">
                    {destinations.map(dest => (
                      <SelectItem key={dest.id} value={dest.id}>{dest.country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedDestination && (
                <div className="col-span-2">
                  <Label>Type de Visa *</Label>
                  <Select value={formData.visa_type_id} onValueChange={(v) => setFormData({...formData, visa_type_id: v})}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-white/10">
                      {selectedDestination.visa_types?.map(vt => (
                        <SelectItem key={vt.id} value={vt.id}>{vt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-2">
                <Label>Nom complet *</Label>
                <Input 
                  value={formData.applicant_name}
                  onChange={(e) => setFormData({...formData, applicant_name: e.target.value})}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  placeholder="Nom du client"
                />
              </div>
              <div>
                <Label>Téléphone *</Label>
                <Input 
                  value={formData.applicant_phone}
                  onChange={(e) => setFormData({...formData, applicant_phone: e.target.value})}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  placeholder="+243..."
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={formData.applicant_email}
                  onChange={(e) => setFormData({...formData, applicant_email: e.target.value})}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  placeholder="email@exemple.com"
                />
              </div>
              <div>
                <Label>Date de voyage</Label>
                <Input 
                  type="date"
                  value={formData.travel_date}
                  onChange={(e) => setFormData({...formData, travel_date: e.target.value})}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label>Urgence</Label>
                <Select value={formData.visa_urgency} onValueChange={(v) => setFormData({...formData, visa_urgency: v})}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10">
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="express">Express</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Input 
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  placeholder="Notes internes..."
                />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" onClick={() => setShowNewVisaModal(false)} className="flex-1 btn-glass">
                Annuler
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1 btn-gold">
                {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Créer le dossier'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  // New Appointment Modal
  const NewAppointmentModal = () => {
    const [formData, setFormData] = useState({
      client_name: '',
      client_phone: '',
      client_email: '',
      service_type: '',
      destination: '',
      preferred_date: '',
      preferred_time: '',
      notes: ''
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.client_name || !formData.client_phone || !formData.service_type || !formData.preferred_date || !formData.preferred_time) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }
      setSubmitting(true);
      try {
        const response = await api.post('/reception/quick-appointment', formData);
        toast.success(`RDV créé ! Réf: ${response.data.reference}`);
        setShowNewAppointmentModal(false);
        fetchData();
      } catch (error) {
        toast.error('Erreur lors de la création');
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <Dialog open={showNewAppointmentModal} onOpenChange={setShowNewAppointmentModal}>
        <DialogContent className="max-w-lg bg-[#0F172A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#D4AF37]" />
              Nouveau Rendez-vous
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="new-appointment-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nom du client *</Label>
                <Input 
                  value={formData.client_name}
                  onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label>Téléphone *</Label>
                <Input 
                  value={formData.client_phone}
                  onChange={(e) => setFormData({...formData, client_phone: e.target.value})}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label>Type de service *</Label>
                <Select value={formData.service_type} onValueChange={(v) => setFormData({...formData, service_type: v})}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10">
                    <SelectItem value="visa_schengen">Visa Schengen</SelectItem>
                    <SelectItem value="visa_canada">Visa Canada</SelectItem>
                    <SelectItem value="visa_usa">Visa USA</SelectItem>
                    <SelectItem value="visa_dubai_investor">Visa Dubaï Investisseur</SelectItem>
                    <SelectItem value="consultation">Consultation Générale</SelectItem>
                    <SelectItem value="other">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input 
                  type="date"
                  value={formData.preferred_date}
                  onChange={(e) => setFormData({...formData, preferred_date: e.target.value})}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label>Heure *</Label>
                <Input 
                  type="time"
                  value={formData.preferred_time}
                  onChange={(e) => setFormData({...formData, preferred_time: e.target.value})}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Input 
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" onClick={() => setShowNewAppointmentModal(false)} className="flex-1 btn-glass">
                Annuler
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1 btn-gold">
                {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Créer le RDV'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  // Payment Confirmation Modal
  const PaymentModal = () => {
    const [amount, setAmount] = useState('');
    const [submitting, setSubmitting] = useState(false);

    if (!selectedApplication) return null;

    const handleConfirm = async () => {
      if (!amount) {
        toast.error('Veuillez entrer le montant reçu');
        return;
      }
      setSubmitting(true);
      try {
        await api.post('/reception/confirm-cash-payment', {
          application_id: selectedApplication.id,
          amount_received: parseFloat(amount)
        });
        toast.success('Paiement confirmé !');
        setShowPaymentModal(false);
        setSelectedApplication(null);
        fetchData();
      } catch (error) {
        toast.error('Erreur lors de la confirmation');
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md bg-[#0F172A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-400" />
              Confirmer le Paiement
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-sm text-gray-400">Client</p>
              <p className="text-white font-medium">{selectedApplication.applicant_name}</p>
              <p className="text-sm text-gray-400 mt-2">Montant dû</p>
              <p className="text-2xl font-bold text-[#D4AF37]">${selectedApplication.total_amount}</p>
            </div>
            <div>
              <Label>Montant reçu ($) *</Label>
              <Input 
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-white/5 border-white/10 text-white mt-1 text-lg"
                placeholder="0.00"
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setShowPaymentModal(false)} className="flex-1 btn-glass">
                Annuler
              </Button>
              <Button onClick={handleConfirm} disabled={submitting} className="flex-1 bg-green-600 hover:bg-green-700">
                {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Confirmer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Prospect Link Modal
  const ProspectModal = () => {
    const [name, setName] = useState('');
    const [contact, setContact] = useState('');
    const [sendVia, setSendVia] = useState('whatsapp');
    const [submitting, setSubmitting] = useState(false);
    const [createdLink, setCreatedLink] = useState(null);

    const handleCreate = async () => {
      if (!name || !contact) {
        toast.error('Veuillez remplir tous les champs');
        return;
      }
      setSubmitting(true);
      try {
        const response = await api.post(`/reception/prospect-link?prospect_name=${encodeURIComponent(name)}&contact=${encodeURIComponent(contact)}&send_via=${sendVia}`);
        setCreatedLink(response.data);
        toast.success('Lien créé !');
      } catch (error) {
        toast.error('Erreur lors de la création');
      } finally {
        setSubmitting(false);
      }
    };

    const copyLink = () => {
      navigator.clipboard.writeText(createdLink.form_url);
      toast.success('Lien copié !');
    };

    return (
      <Dialog open={showProspectModal} onOpenChange={setShowProspectModal}>
        <DialogContent className="max-w-md bg-[#0F172A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-[#D4AF37]" />
              Accueil Prospect
            </DialogTitle>
          </DialogHeader>
          {createdLink ? (
            <div className="space-y-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <p className="text-green-400 font-medium mb-2">Lien créé avec succès !</p>
                <div className="bg-black/30 rounded p-2 text-sm break-all">
                  {createdLink.form_url}
                </div>
              </div>
              <div className="flex gap-3">
                <Button onClick={copyLink} className="flex-1 btn-gold">
                  Copier le lien
                </Button>
                <Button onClick={() => { setShowProspectModal(false); setCreatedLink(null); }} className="flex-1 btn-glass">
                  Fermer
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Nom du prospect *</Label>
                <Input 
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  placeholder="Jean Dupont"
                />
              </div>
              <div>
                <Label>Contact ({sendVia === 'whatsapp' ? 'Téléphone' : 'Email'}) *</Label>
                <Input 
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  placeholder={sendVia === 'whatsapp' ? '+243...' : 'email@exemple.com'}
                />
              </div>
              <div>
                <Label>Envoyer via</Label>
                <div className="flex gap-2 mt-1">
                  <Button 
                    type="button"
                    onClick={() => setSendVia('whatsapp')}
                    className={`flex-1 ${sendVia === 'whatsapp' ? 'btn-gold' : 'btn-glass'}`}
                  >
                    WhatsApp
                  </Button>
                  <Button 
                    type="button"
                    onClick={() => setSendVia('email')}
                    className={`flex-1 ${sendVia === 'email' ? 'btn-gold' : 'btn-glass'}`}
                  >
                    Email
                  </Button>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button onClick={() => setShowProspectModal(false)} className="flex-1 btn-glass">
                  Annuler
                </Button>
                <Button onClick={handleCreate} disabled={submitting} className="flex-1 btn-gold">
                  {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Créer le lien'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#D4AF37] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0F172A] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Global Wings" className="h-20" />
          </div>
          
          <div className="flex items-center gap-4">
            <div className="hidden md:block text-right">
              <p className="text-sm text-white">{user?.full_name}</p>
              <p className="text-xs text-gray-400">Réception</p>
            </div>
            <Button onClick={logout} variant="ghost" size="sm" className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Welcome */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Bonjour, {user?.full_name?.split(' ')[0]} 👋</h1>
            <p className="text-gray-400">Accueil et réception des clients</p>
          </div>
          <Button onClick={fetchData} variant="outline" className="border-white/10 text-gray-300">
            <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button onClick={() => setShowNewVisaModal(true)} className="h-auto py-4 flex flex-col items-center gap-2 btn-gold" data-testid="btn-new-visa">
            <FileText className="w-6 h-6" />
            <span>Nouveau Dossier</span>
          </Button>
          <Button onClick={() => setShowNewAppointmentModal(true)} className="h-auto py-4 flex flex-col items-center gap-2 btn-glass text-white" data-testid="btn-new-appointment">
            <Calendar className="w-6 h-6" />
            <span>Nouveau RDV</span>
          </Button>
          <Button onClick={() => setShowProspectModal(true)} className="h-auto py-4 flex flex-col items-center gap-2 btn-glass text-white" data-testid="btn-new-prospect">
            <User className="w-6 h-6" />
            <span>Accueil Prospect</span>
          </Button>
          <Button onClick={() => {}} className="h-auto py-4 flex flex-col items-center gap-2 btn-glass text-white" data-testid="btn-print">
            <Printer className="w-6 h-6" />
            <span>Imprimer Reçu</span>
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#0F172A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.todays_appointments || 0}</p>
                  <p className="text-xs text-gray-400">RDV aujourd'hui</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0F172A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.todays_payments || 0}</p>
                  <p className="text-xs text-gray-400">Paiements reçus</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0F172A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.pending_appointments || 0}</p>
                  <p className="text-xs text-gray-400">RDV en attente</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0F172A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.todays_dossiers || 0}</p>
                  <p className="text-xs text-gray-400">Dossiers créés</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="bg-[#0F172A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Search className="w-5 h-5" />
              Recherche Client
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Nom, téléphone ou référence..."
                className="bg-white/5 border-white/10 text-white"
                data-testid="search-input"
              />
              <Button onClick={handleSearch} className="btn-gold">
                <Search className="w-4 h-4" />
              </Button>
            </div>
            
            {searchResults && (
              <div className="mt-4 space-y-4">
                {searchResults.applications?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-2">Dossiers Visa</h4>
                    <div className="space-y-2">
                      {searchResults.applications.map(app => (
                        <div key={app.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                          <div>
                            <p className="text-white font-medium">{app.applicant_name}</p>
                            <p className="text-sm text-gray-400">{app.destination} • {app.id.slice(0,8).toUpperCase()}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={app.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}>
                              {app.payment_status === 'paid' ? 'Payé' : 'En attente'}
                            </Badge>
                            {app.payment_status !== 'paid' && (
                              <Button 
                                size="sm" 
                                onClick={() => { setSelectedApplication(app); setShowPaymentModal(true); }}
                                className="bg-green-600 hover:bg-green-700 text-xs"
                              >
                                Paiement
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {searchResults.appointments?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-2">Rendez-vous</h4>
                    <div className="space-y-2">
                      {searchResults.appointments.map(apt => (
                        <div key={apt.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                          <div>
                            <p className="text-white font-medium">{apt.client_name}</p>
                            <p className="text-sm text-gray-400">{apt.service_type} • {apt.preferred_date}</p>
                          </div>
                          <Badge className="bg-blue-500/20 text-blue-400">{apt.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {searchResults.applications?.length === 0 && searchResults.appointments?.length === 0 && (
                  <p className="text-center text-gray-400 py-4">Aucun résultat trouvé</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Today's Appointments */}
        <Card className="bg-[#0F172A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#D4AF37]" />
              Rendez-vous du jour
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todaysAppointments.length === 0 ? (
              <p className="text-center text-gray-400 py-8">Aucun rendez-vous prévu aujourd'hui</p>
            ) : (
              <div className="space-y-3">
                {todaysAppointments.map(apt => (
                  <div key={apt.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                        <Clock className="w-6 h-6 text-[#D4AF37]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{apt.client_name}</p>
                        <p className="text-sm text-gray-400">{apt.service_type}</p>
                        <p className="text-xs text-gray-500">{apt.client_phone}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#D4AF37]">{apt.preferred_time}</p>
                      <Badge className={
                        apt.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                        apt.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-gray-500/20 text-gray-400'
                      }>
                        {apt.status === 'confirmed' ? 'Confirmé' : apt.status === 'pending' ? 'En attente' : apt.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Modals */}
      <NewVisaModal />
      <NewAppointmentModal />
      <PaymentModal />
      <ProspectModal />
    </div>
  );
};

export default ReceptionDashboard;
